import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Package,
  User,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  ArrowLeft,
  DollarSign,
  Phone,
  Shield,
  Calendar,
  ChevronRight,
  Tag,
  Palette,
  Ruler
} from 'lucide-react'

interface OrderDetails {
  id: string
  buyer_id: string
  seller_id: string
  product_id: string
  final_price: number
  status: 'pending' | 'confirmed' | 'paid' | 'delivered' | 'cancelled'
  delivery_type: 'standard' | 'express' | 'pickup'
  delivery_address: string
  confirmation_code: string | null
  payment_type: 'online' | 'in_person'
  created_at: string
  updated_at: string
  product: {
    title: string
    images: string[]
    price: number
    selectedColor?: string
    selectedSize?: string
  }
  seller: {
    full_name: string | null
    email: string | null
    phone_number: string | null
  }
  buyer: {
    full_name: string | null
    email: string | null
    phone_number: string | null
  }
}

interface DeliveryDetails {
  id: string
  order_id: string
  seller_id: string
  delivery_partner_id: string | null
  status: string
  current_step: string
  pickup_instructions: string | null
  delivery_notes: string | null
  created_at: string
  updated_at: string
  step_history: {
    step: string
    timestamp: string
    metadata: any
  }[]
  delivery_partner: {
    applicant_name: string
    applicant_email: string
    vehicle_type: string
    applicant_phone: string
  } | null
}

export function OrderTrackingPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [order, setOrder] = useState<OrderDetails | null>(null)
  const [delivery, setDelivery] = useState<DeliveryDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    if (id) {
      fetchOrderDetails()
    }
  }, [id, user, navigate])

  const fetchOrderDetails = async () => {
    if (!id || !user) return

    try {
      setLoading(true)
      
      // Fetch order details
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select(`
          *,
          product:products (
            title,
            images,
            price
          ),
          seller:profiles!seller_id (
            full_name,
            email,
            phone_number
          ),
          buyer:profiles!buyer_id (
            full_name,
            email,
            phone_number
          )
        `)
        .eq('id', id)
        .single()

      if (orderError) throw orderError

      // Check if the user is authorized to view this order
      const isOwner = orderData.buyer_id === user.id
      const isSeller = orderData.seller_id === user.id
      const isAdmin = user.role === 'admin'
      
      if (!isOwner && !isSeller && !isAdmin) {
        throw new Error('Vous n\'êtes pas autorisé à voir cette commande')
      }

      setOrder(orderData)

      // Fetch delivery details if they exist
      const { data: deliveryData, error: deliveryError } = await supabase
        .from('delivery_requests')
        .select(`
          *,
          delivery_partner:delivery_partners (
            applicant_name,
            applicant_email,
            vehicle_type,
            applicant_phone
          )
        `)
        .eq('order_id', id)
        .maybeSingle()

      if (deliveryError && deliveryError.code !== 'PGRST116') {
        throw deliveryError
      }

      if (deliveryData) {
        setDelivery(deliveryData)
      }
    } catch (err) {
      console.error('Error fetching order details:', err)
      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors du chargement de la commande')
    } finally {
      setLoading(false)
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'En attente'
      case 'confirmed': return 'Confirmée'
      case 'paid': return 'Payée'
      case 'delivered': return 'Livrée'
      case 'cancelled': return 'Annulée'
      default: return status
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'confirmed': return 'bg-blue-100 text-blue-800'
      case 'paid': return 'bg-green-100 text-green-800'
      case 'delivered': return 'bg-purple-100 text-purple-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="h-5 w-5" />
      case 'confirmed': return <CheckCircle className="h-5 w-5" />
      case 'paid': return <DollarSign className="h-5 w-5" />
      case 'delivered': return <Package className="h-5 w-5" />
      case 'cancelled': return <XCircle className="h-5 w-5" />
      default: return <Clock className="h-5 w-5" />
    }
  }

  const getDeliveryStepLabel = (step: string) => {
    switch (step) {
      case 'pending': return 'En attente'
      case 'accepted': return 'Acceptée'
      case 'preparing': return 'En préparation'
      case 'pickup_ready': return 'Prêt pour récupération'
      case 'picked_up': return 'Récupéré'
      case 'in_transit': return 'En transit'
      case 'arrived': return 'Arrivé'
      case 'delivered': return 'Livré'
      case 'cancelled': return 'Annulé'
      default: return step
    }
  }

  const getDeliveryStepIcon = (step: string) => {
    switch (step) {
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />
      case 'accepted': return <CheckCircle className="h-5 w-5 text-blue-500" />
      case 'preparing': return <Package className="h-5 w-5 text-purple-500" />
      case 'pickup_ready': return <Package className="h-5 w-5 text-indigo-500" />
      case 'picked_up': return <Truck className="h-5 w-5 text-orange-500" />
      case 'in_transit': return <Truck className="h-5 w-5 text-blue-500" />
      case 'arrived': return <MapPin className="h-5 w-5 text-green-500" />
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />
      default: return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getDeliverySteps = () => {
    const steps = [
      { id: 'pending', label: 'En attente', icon: Clock, color: 'text-yellow-500' },
      { id: 'accepted', label: 'Acceptée', icon: CheckCircle, color: 'text-blue-500' },
      { id: 'preparing', label: 'En préparation', icon: Package, color: 'text-purple-500' },
      { id: 'pickup_ready', label: 'Prêt pour récupération', icon: Package, color: 'text-indigo-500' },
      { id: 'picked_up', label: 'Récupéré', icon: Truck, color: 'text-orange-500' },
      { id: 'in_transit', label: 'En transit', icon: Truck, color: 'text-blue-500' },
      { id: 'arrived', label: 'Arrivé', icon: MapPin, color: 'text-green-500' },
      { id: 'delivered', label: 'Livré', icon: CheckCircle, color: 'text-green-500' }
    ]

    if (!delivery) return steps

    // Find the current step index
    const currentStepIndex = steps.findIndex(step => step.id === delivery.current_step)
    if (currentStepIndex === -1) return steps

    // Mark steps as completed or current
    return steps.map((step, index) => ({
      ...step,
      status: index < currentStepIndex ? 'completed' : index === currentStepIndex ? 'current' : 'upcoming'
    }))
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center text-red-500 mb-4">
              <XCircle className="h-6 w-6 mr-2" />
              <h2 className="text-lg font-medium">Erreur</h2>
            </div>
            <p className="text-gray-700">{error || 'Commande introuvable'}</p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => navigate('/profile')}
            >
              Retour au profil
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="flex items-center text-gray-600"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Suivi de commande
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  Commande #{order.id.slice(0, 8)}
                </p>
              </div>
              <div className="mt-2 sm:mt-0">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                  {getStatusIcon(order.status)}
                  <span className="ml-1">{getStatusLabel(order.status)}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            {/* Order Summary */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Résumé de la commande
              </h2>
              <div className="flex items-start">
                <div className="h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                  <img
                    src={order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                    alt={order.product?.title}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-base font-medium text-gray-900">
                    {order.product?.title}
                  </h3>
                  <div className="mt-1 flex items-center text-sm text-gray-500">
                    <DollarSign className="h-4 w-4 mr-1" />
                    {order.final_price.toFixed(2)}
                    {order.final_price !== order.product?.price && (
                      <span className="ml-2 line-through text-gray-400">
                        ${order.product?.price.toFixed(2)}
                      </span>
                    )}
                  </div>
                  
                  {/* Product Options */}
                  <div className="mt-2 space-y-1">
                    {order.product?.selectedColor && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Palette className="h-4 w-4 mr-1 text-gray-400" />
                        <span>Couleur: {order.product.selectedColor}</span>
                      </div>
                    )}
                    
                    {order.product?.selectedSize && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Ruler className="h-4 w-4 mr-1 text-gray-400" />
                        <span>Taille: {order.product.selectedSize}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-2 flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-500">
                      Commandé le {formatDate(order.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Informations de paiement
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Méthode de paiement</p>
                    <p className="mt-1 text-sm text-gray-900">
                      {order.payment_type === 'online' ? 'Paiement en ligne' : 'Paiement à la livraison'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Montant total</p>
                    <p className="mt-1 text-sm text-gray-900">${order.final_price.toFixed(2)}</p>
                  </div>
                </div>

                {/* Confirmation Code for In-Person Payment */}
                {order.payment_type === 'in_person' && order.confirmation_code && (
                  <div className="mt-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="flex flex-col">
                      <div className="flex items-center mb-2">
                        <Shield className="h-4 w-4 text-yellow-500 mr-2 flex-shrink-0" />
                        <span className="text-sm font-medium text-yellow-700">Code de confirmation:</span>
                      </div>
                      <div className="bg-yellow-100 px-3 py-2 rounded text-center">
                        <span className="text-xl font-bold tracking-wider text-yellow-800 break-all">
                          {order.confirmation_code}
                        </span>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-yellow-600">
                      Présentez ce code au livreur lors de la réception pour confirmer la livraison.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Information */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Informations de livraison
              </h2>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Adresse de livraison</p>
                    <p className="mt-1 text-sm text-gray-900 break-words">{order.delivery_address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Type de livraison</p>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{order.delivery_type}</p>
                  </div>
                </div>

                {delivery?.delivery_partner && (
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <p className="text-sm font-medium text-gray-500">Livreur</p>
                    <div className="mt-2 flex items-start">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">
                          {delivery.delivery_partner.applicant_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {delivery.delivery_partner.applicant_phone}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">
                          Véhicule: {delivery.delivery_partner.vehicle_type}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Delivery Tracking */}
            {delivery && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Suivi de livraison
                </h2>
                <div className="relative">
                  <div className="w-full overflow-x-visible">
                    {/* Vertical line connecting steps */}
                    <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    
                    {/* Delivery steps */}
                    <div className="space-y-6">
                      {getDeliverySteps().map((step, index) => {
                        const StepIcon = step.icon
                        const isCompleted = step.status === 'completed'
                        const isCurrent = step.status === 'current'
                        
                        // Find timestamp for this step from history if available
                        const stepHistory = delivery.step_history?.find(h => h.step === step.id)
                        const stepTime = stepHistory ? new Date(stepHistory.timestamp).toLocaleString() : null
                        
                        return (
                          <div key={step.id} className="relative flex items-start">
                            <div className={`absolute left-0 h-14 w-14 rounded-full flex items-center justify-center ${
                              isCompleted 
                                ? 'bg-primary text-white' 
                                : isCurrent
                                  ? 'bg-white border-2 border-primary text-primary'
                                  : 'bg-white border-2 border-gray-200 text-gray-400'
                            }`}>
                              <StepIcon className="h-6 w-6" />
                            </div>
                            <div className="ml-16">
                              <h3 className={`text-base font-medium ${
                                isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-500'
                              }`}>
                                {step.label}
                              </h3>
                              {stepTime && (
                                <p className="mt-1 text-sm text-gray-500">
                                  {stepTime}
                                </p>
                              )}
                              {isCurrent && (
                                <p className="mt-1 text-sm text-primary">
                                  En cours
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Contacts
            </h2>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Vendeur</h3>
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-900">
                    {order.seller?.full_name || 'Vendeur'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.seller?.email}
                  </p>
                  {order.seller?.phone_number && (
                    <p className="text-sm text-gray-500">
                      {order.seller?.phone_number}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Acheteur</h3>
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-900">
                    {order.buyer?.full_name || 'Acheteur'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.buyer?.email}
                  </p>
                  {order.buyer?.phone_number && (
                    <p className="text-sm text-gray-500">
                      {order.buyer?.phone_number}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}